import React, { useState, useEffect } from 'react';
import { User, Users, Key, X, Search, Plus, LogIn, LogOut, Shield } from 'lucide-react';
import { authAPI } from '../services/api';

const colors = {
  black: '#000000',
  white: '#ffffff',
  gray: {
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  purple: {
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',
  },
  orange: {
    500: '#F97316',
  },
  green: {
    500: '#22C55E',
  },
  red: {
    500: '#EF4444',
  }
};

const AdminTestingPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [impersonating, setImpersonating] = useState(false);

  // Login state
  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // Accounts list
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Create test account
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    role: 'user',
    password: 'test123'
  });

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      checkCurrentUser();
    }
  }, []);

  const checkCurrentUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.success) {
        setCurrentUser(response.account);
        setIsLoggedIn(true);
        setImpersonating(localStorage.getItem('impersonating') === 'true');
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('impersonating');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await authAPI.login(loginForm.identifier, loginForm.password);
      if (response.success) {
        localStorage.setItem('authToken', response.token);
        setCurrentUser(response.account);
        setIsLoggedIn(true);
        setLoginForm({ identifier: '', password: '' });

        // Load accounts if admin
        if (response.account.role === 'admin') {
          loadAccounts();
        }
      }
    } catch (error) {
      setLoginError(error.response?.data?.error || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('impersonating');
    setIsLoggedIn(false);
    setCurrentUser(null);
    setImpersonating(false);
    setAccounts([]);
  };

  const loadAccounts = async () => {
    try {
      const response = await authAPI.getAccounts({ search: searchTerm, role: roleFilter });
      if (response.success) {
        setAccounts(response.accounts);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const handleImpersonate = async (accountId) => {
    try {
      const response = await authAPI.impersonate(accountId);
      if (response.success) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('originalAdmin', response.adminId);
        setCurrentUser(response.account);
        setImpersonating(true);
        alert(`✅ Now testing as: ${response.account.username}`);
        window.location.reload(); // Reload to apply new auth state
      }
    } catch (error) {
      alert('Failed to impersonate: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleCreateTestAccount = async (e) => {
    e.preventDefault();

    try {
      const response = await authAPI.createTestAccount(createForm);
      if (response.success) {
        alert(`✅ Test account created: ${response.account.username}`);
        setCreateForm({ username: '', email: '', role: 'user', password: 'test123' });
        setShowCreateForm(false);
        loadAccounts();
      }
    } catch (error) {
      alert('Failed to create account: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  useEffect(() => {
    if (isOpen && isLoggedIn && currentUser?.role === 'admin') {
      loadAccounts();
    }
  }, [isOpen, searchTerm, roleFilter]);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: colors.purple[500],
          border: 'none',
          color: colors.white,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
        }}
      >
        <Shield size={28} />
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: '450px',
      maxHeight: '600px',
      backgroundColor: colors.gray[900],
      borderRadius: '16px',
      border: `1px solid ${colors.gray[800]}`,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: `1px solid ${colors.gray[800]}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.purple[600]
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shield size={20} style={{ color: colors.white }} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: colors.white }}>
            Admin Testing Panel
          </h3>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            background: 'none',
            border: 'none',
            color: colors.white,
            cursor: 'pointer',
            padding: '4px',
            display: 'flex'
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {!isLoggedIn ? (
          // Login Form
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: colors.white, fontSize: '14px', fontWeight: '600' }}>
                Admin Email/Username
              </label>
              <input
                type="text"
                value={loginForm.identifier}
                onChange={(e) => setLoginForm({ ...loginForm, identifier: e.target.value })}
                placeholder="admin@example.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.gray[700]}`,
                  borderRadius: '8px',
                  backgroundColor: colors.gray[800],
                  color: colors.white,
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: colors.white, fontSize: '14px', fontWeight: '600' }}>
                Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.gray[700]}`,
                  borderRadius: '8px',
                  backgroundColor: colors.gray[800],
                  color: colors.white,
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {loginError && (
              <div style={{
                padding: '10px',
                backgroundColor: colors.red[500] + '20',
                border: `1px solid ${colors.red[500]}`,
                borderRadius: '8px',
                color: colors.red[500],
                fontSize: '13px'
              }}>
                {loginError}
              </div>
            )}

            <button
              type="submit"
              style={{
                padding: '12px',
                backgroundColor: colors.purple[500],
                border: 'none',
                borderRadius: '8px',
                color: colors.white,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <LogIn size={16} />
              Login as Admin
            </button>
          </form>
        ) : (
          <>
            {/* Current User Info */}
            <div style={{
              padding: '12px',
              backgroundColor: impersonating ? colors.orange[500] + '20' : colors.green[500] + '20',
              border: `1px solid ${impersonating ? colors.orange[500] : colors.green[500]}`,
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <User size={16} style={{ color: impersonating ? colors.orange[500] : colors.green[500] }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: colors.white }}>
                  {impersonating ? '🎭 Testing as:' : '✅ Logged in as:'}
                </span>
              </div>
              <div style={{ fontSize: '14px', color: colors.white, fontWeight: '700' }}>
                {currentUser.username} ({currentUser.role})
              </div>
              <button
                onClick={handleLogout}
                style={{
                  marginTop: '8px',
                  padding: '6px 12px',
                  backgroundColor: colors.gray[800],
                  border: `1px solid ${colors.gray[700]}`,
                  borderRadius: '6px',
                  color: colors.white,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <LogOut size={14} />
                {impersonating ? 'Stop Testing' : 'Logout'}
              </button>
            </div>

            {currentUser?.role === 'admin' && !impersonating && (
              <>
                {/* Controls */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: colors.purple[500],
                      border: 'none',
                      borderRadius: '8px',
                      color: colors.white,
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={16} />
                    Create Test User
                  </button>
                </div>

                {/* Create Test Account Form */}
                {showCreateForm && (
                  <form onSubmit={handleCreateTestAccount} style={{
                    padding: '12px',
                    backgroundColor: colors.gray[800],
                    borderRadius: '8px',
                    marginBottom: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <input
                      type="text"
                      placeholder="Username"
                      value={createForm.username}
                      onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                      style={{
                        padding: '8px 10px',
                        border: `1px solid ${colors.gray[700]}`,
                        borderRadius: '6px',
                        backgroundColor: colors.gray[900],
                        color: colors.white,
                        fontSize: '13px',
                        outline: 'none'
                      }}
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      style={{
                        padding: '8px 10px',
                        border: `1px solid ${colors.gray[700]}`,
                        borderRadius: '6px',
                        backgroundColor: colors.gray[900],
                        color: colors.white,
                        fontSize: '13px',
                        outline: 'none'
                      }}
                      required
                    />
                    <select
                      value={createForm.role}
                      onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                      style={{
                        padding: '8px 10px',
                        border: `1px solid ${colors.gray[700]}`,
                        borderRadius: '6px',
                        backgroundColor: colors.gray[900],
                        color: colors.white,
                        fontSize: '13px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="user">User</option>
                      <option value="creator">Creator</option>
                    </select>
                    <button
                      type="submit"
                      style={{
                        padding: '8px',
                        backgroundColor: colors.green[500],
                        border: 'none',
                        borderRadius: '6px',
                        color: colors.white,
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      Create Account
                    </button>
                  </form>
                )}

                {/* Search & Filter */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={14} style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: colors.gray[500]
                    }} />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px 8px 32px',
                        border: `1px solid ${colors.gray[700]}`,
                        borderRadius: '8px',
                        backgroundColor: colors.gray[800],
                        color: colors.white,
                        fontSize: '13px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{
                      padding: '8px 10px',
                      border: `1px solid ${colors.gray[700]}`,
                      borderRadius: '8px',
                      backgroundColor: colors.gray[800],
                      color: colors.white,
                      fontSize: '13px',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All</option>
                    <option value="user">Users</option>
                    <option value="creator">Creators</option>
                    <option value="admin">Admins</option>
                  </select>
                </div>

                {/* Accounts List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {accounts.map(account => (
                    <div
                      key={account._id}
                      style={{
                        padding: '10px',
                        backgroundColor: colors.gray[800],
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: colors.white }}>
                          {account.username}
                        </div>
                        <div style={{ fontSize: '11px', color: colors.gray[400] }}>
                          {account.email} • {account.role}
                        </div>
                      </div>
                      <button
                        onClick={() => handleImpersonate(account._id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: colors.orange[500],
                          border: 'none',
                          borderRadius: '6px',
                          color: colors.white,
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Key size={12} />
                        Test
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminTestingPanel;
